import React, { useEffect, useMemo } from "react";
import { App } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import AppControl from "../components/";
import { createYupSchema } from "../utilities/";
import { fetchRegisterForm, fetchWorkCategories } from "../store/slice/common";
import { registerUser } from "../store/slice/auth";
import AppNotificationContainer from "../components/controls/AppNotificationContainer";

const APPLICATION_NAME = "fixilo";

function Register() {
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { page, isLoading, failedMessage, workCategories } = useSelector(
    (state) => state.common,
  );
  const pageControls = page?.fields;

  // Fetch form data on component mount
  useEffect(() => {
    dispatch(fetchRegisterForm());
    dispatch(fetchWorkCategories());
  }, [dispatch]);

  // Build dropdown values for WorkCategories selector
  const dropdownValues = useMemo(() => {
    if (!workCategories || !Array.isArray(workCategories)) {
      console.log(
        "WorkCategories not available or not an array:",
        workCategories,
      );
      return {};
    }
    return {
      workCategories: workCategories.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    };
  }, [workCategories]);
  // Memoize initial values and validation schema
  const { initialValues, validationSchema } = useMemo(() => {
    if (!pageControls) {
      return { initialValues: {}, validationSchema: Yup.object().shape({}) };
    }

    const values = pageControls.reduce(
      (acc, control) => ({
        ...acc,
        [control.fieldId]: control.defaultValue,
      }),
      {},
    );

    const yupSchema = pageControls.reduce(createYupSchema, {});
    const schema = Yup.object().shape(yupSchema);

    return { initialValues: values, validationSchema: schema };
  }, [pageControls]);

  const handleSubmit = async (values) => {
    const submitData = {
      ...values,
      applicationName: APPLICATION_NAME,
    };

    const resultAction = await dispatch(registerUser(submitData));

    if (registerUser.fulfilled.match(resultAction)) {
      navigate("/register-success");
    } else if (resultAction.payload?.field_errors) {
      // Handle field-specific errors if needed
      message.error("Validation error occurred");
    } else {
      message.error(`Save failed: ${resultAction.error?.message}`);
    }
  };

  const renderFormControls = () => (
    <div className="flex-col p-3">
      {pageControls?.map((control) => (
        <AppControl
          key={control.fieldId}
          control={control.type}
          label={control.label}
          name={control.fieldId}
          placeholder={control.placeholder}
          options={
            control.options ? dropdownValues[control.fieldId] : undefined
          }
        />
      ))}
    </div>
  );

  const renderFormActions = (formik) => (
    <>
      <AppControl
        control="button"
        type="submit"
        name="Register"
        size="md"
        color="orange"
        disabled={!formik.isValid || isLoading}
        loading={isLoading}
      />
      <AppControl
        control="button"
        type="reset"
        name="Cancel"
        size="md"
        color="blue"
        onClick={() => navigate("/users")}
      />
    </>
  );

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <div className="flex justify-center items-center min-h-screen">
          <div className="block max-w-md rounded-lg bg-white p-6 shadow-lg">
            <h5 className="mb-3 text-xl font-medium leading-tight text-neutral-800 text-center">
              Register
            </h5>
            <Form>
              {renderFormControls()}
              {renderFormActions(formik)}
            </Form>
          </div>
          <AppNotificationContainer loading={isLoading} error={failedMessage} />
        </div>
      )}
    </Formik>
  );
}

export default Register;
